<script setup lang="ts">
import { computed } from 'vue';
import { useLayout } from './composables/layout';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useAuthStore } from '@/stores/authStore';
import { useProductStore } from '@/stores/productStore';
import { useLaunchChecklistStore } from '@/stores/launchChecklistStore';
import type { MenuItem } from '@/types/Menu';
import AppSubMenu from './AppSubMenu.vue';

const {layoutConfig} = useLayout();
const { isFeatureEnabled } = useFeatureFlags();
const authStore = useAuthStore();
const productStore = useProductStore();
const launchChecklist = useLaunchChecklistStore();

// Single source of truth for the sidebar. Static mode renders each group's label
// as a section header; slim mode has no room for headers and flattens the same
// items into one icon-only list.
// The first group is deliberately unlabelled: an empty label suppresses the
// header, which puts Dashboard on its own above the named sections. It can't be
// a top-level item instead — the theme hides a root item's own anchor
// (_sidebar_vertical.scss, `.layout-root-menuitem > a`) because it assumes every
// root is a section header.
const menuGroups: MenuItem[] = [
    {
        label: '',
        items: [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-home',
                to: '/'
            },
            {
                label: 'Reports',
                icon: 'pi pi-fw pi-chart-bar',
                to: '/reports'
            }
        ]
    },
    {
        label: 'Sales',
        items: [
            {
                label: 'Customers',
                icon: 'pi pi-fw pi-users',
                to: '/customers'
            },
            {
                label: 'Orders',
                icon: 'pi pi-fw pi-shopping-cart',
                to: '/orders'
            },
            {
                label: 'Invoices',
                icon: 'pi pi-fw pi-file',
                to: '/invoices'
            },
            {
                label: 'Carts',
                icon: 'pi pi-fw pi-shopping-bag',
                to: '/carts'
            },
            {
                label: 'Campaigns',
                icon: 'pi pi-fw pi-megaphone',
                to: '/campaigns',
                featureFlag: 'campaigns'
            },
            {
                label: 'Contacts',
                icon: 'pi pi-fw pi-id-card',
                to: '/contacts',
                featureFlag: 'contacts-page'
            },
            {
                label: 'Tasks',
                icon: 'pi pi-fw pi-check-square',
                to: '/tasks',
                featureFlag: 'tasks'
            },
            {
                label: 'Forecast Models',
                icon: 'pi pi-fw pi-chart-line',
                to: '/forecast-models',
                featureFlag: 'sales-forecasting'
            }
        ]
    },
    {
        label: 'Catalog',
        items: [
            {
                label: 'Products',
                icon: 'pi pi-fw pi-list',
                to: '/products'
            },
            {
                // The manufacturers an agency represents; Agency organizations only (see isVisible).
                label: 'Vendors',
                icon: 'pi pi-fw pi-building',
                to: '/vendors'
            },
            {
                // Shown only once the organization turns product variants on (Settings > Product
                // Variants); see isVisible below.
                label: 'Product Families',
                icon: 'pi pi-fw pi-th-large',
                to: '/product-families'
            },
            {
                label: 'Categories',
                icon: 'pi pi-fw pi-sitemap',
                to: '/categories'
            },
            {
                // Categories previously held pi-tags; it moves to the tree icon that matches its
                // hierarchy, leaving the tag icon for the page that is actually about tags.
                label: 'Tags',
                icon: 'pi pi-fw pi-tags',
                to: '/tags'
            },
            {
                label: 'Images',
                icon: 'pi pi-fw pi-image',
                to: '/images'
            },
            {
                label: 'Flipbooks',
                icon: 'pi pi-fw pi-book',
                to: '/flipbooks'
            },
            {
                label: 'Company Docs',
                icon: 'pi pi-fw pi-folder-open',
                to: '/company-docs'
            }
        ]
    },
    {
        label: 'Storefront',
        items: [
            {
                label: 'Storefront Requests',
                icon: 'pi pi-fw pi-user-plus',
                to: '/access-requests'
            },
            {
                label: 'Storefront Users',
                icon: 'pi pi-fw pi-users',
                to: '/storefront-users'
            },
            {
                label: 'Research Settings',
                icon: 'pi pi-fw pi-search',
                to: '/research-settings'
            },
            {
                label: 'Website API Key',
                icon: 'pi pi-fw pi-key',
                to: '/website-api-key'
            }
        ]
    },
    {
        label: 'Data',
        items: [
            {
                label: 'Imports',
                icon: 'pi pi-fw pi-upload',
                to: '/data'
            },
            {
                label: 'Exports',
                icon: 'pi pi-fw pi-download',
                to: '/exports'
            }
        ]
    },
    {
        label: 'Administration',
        items: [
            {
                label: 'Admin Users',
                icon: 'pi pi-fw pi-user-edit',
                to: '/users'
            },
            {
                label: 'Sales Reps',
                icon: 'pi pi-fw pi-user',
                to: '/sales-reps'
            },
            {
                label: 'Email Deliveries',
                icon: 'pi pi-fw pi-envelope',
                to: '/email-deliveries'
            },
            {
                label: 'Settings',
                icon: 'pi pi-fw pi-cog',
                to: '/settings'
            }
        ]
    }
];

// These modes render a root item as an icon/link and hide its label
// (`.layout-menuitem-root-text { display: none }`), so they can't show section
// headers — they get the same items flattened instead.
const flatMenuModes = ['slim', 'slim-plus', 'horizontal'];
const useFlatModel = computed(() => flatMenuModes.includes(layoutConfig.menuMode));

// Items carrying a featureFlag key are only shown when that Kinde flag is enabled
const isEnabled = (item: MenuItem) => !item.featureFlag || isFeatureEnabled(item.featureFlag, false);

// Pages gated by an organization setting rather than a platform flag. A tenant that has not
// turned product variants on never sees the families page, so the sidebar stays exactly as it was.
const organizationGates: Record<string, () => boolean> = {
    '/product-families': () => !!authStore.organization?.features?.productVariants,
    // Vendors exist only for an Agency organization (Organization.Type); a Supplier never sees the entry.
    '/vendors': () => authStore.isAgency
};
const isVisible = (item: MenuItem) => !item.to || !(item.to in organizationGates) || organizationGates[item.to]();

// Warnings a page can raise on its own sidebar entry, keyed by route so the menu above stays a
// plain declaration and never has to know where a number comes from. Empty categories are easy to
// accumulate and invisible from anywhere else: products move between paths and the abandoned ones
// stay in storefront and sales-app navigation until someone opens Categories and notices. The
// sidebar never fetches the count itself: the Categories page and the edits that can change it do.
const alerts = computed<Record<string, string | undefined>>(() => {
    const emptyCategories = productStore.emptyCategoryCount;

    return {
        '/categories': emptyCategories > 0
            ? `${emptyCategories} ${emptyCategories === 1 ? 'category has' : 'categories have'} no products`
            : undefined
    };
});

const withAlert = (item: MenuItem): MenuItem => {
    // The icon-only modes hide labels because the rail has no room for text; a second icon has
    // nowhere to go either, so they stay as they are.
    if (useFlatModel.value) return item;

    const alert = item.to ? alerts.value[item.to] : undefined;
    const required = !!item.to && launchChecklist.isRouteRequired(item.to);

    if (!alert && !required) return item;
    return { ...item, ...(alert ? { alert } : {}), ...(required ? { required } : {}) };
};

const model = computed(() =>
    menuGroups
        .map((group) => ({ ...group, items: (group.items ?? []).filter(isEnabled).filter(isVisible).map(withAlert) }))
        .filter((group) => group.items.length > 0)
);

const slimModel = computed(() => model.value.flatMap((group) => group.items));
</script>

<template>
    <AppSubMenu :model="useFlatModel ? slimModel : model" />
</template>

<style lang="scss" scoped></style>
